// AGC: useBiometric - Biometric authentication hook
// AGC: Uses Native Biometric plugin for Capacitor mobile apps
// AGC: Falls back to WebAuthn for web browsers

import { useState, useCallback, useEffect } from 'react'
import { Capacitor } from '@capacitor/core'
import { NativeBiometric, BiometryType } from '@capgo/capacitor-native-biometric'
import { updateBiometricStatus } from '../services/firebase-auth'

// AGC: Server name for credential storage
const BIOMETRIC_SERVER = 'app.droidfarm.com'

// AGC: Types for biometric operations
interface BiometricState {
  isAvailable: boolean
  isEnrolled: boolean
  isLoading: boolean
  error: string | null
  biometricType: 'fingerprint' | 'face' | 'iris' | 'unknown' | null
}

interface UseBiometricReturn extends BiometricState {
  checkAvailability: () => Promise<boolean>
  enrollBiometric: (userId: string) => Promise<boolean>
  authenticateWithBiometric: () => Promise<boolean>
  clearError: () => void
}

// AGC: Check if running on native platform (iOS/Android)
const isNativePlatform = (): boolean => {
  return Capacitor.isNativePlatform()
}

// AGC: Check if we're in a secure context (HTTPS or localhost)
const isSecureContext = (): boolean => {
  if (typeof window === 'undefined') return false
  
  // AGC: Native platforms are always secure
  if (isNativePlatform()) return true
  
  // AGC: Check browser's secure context flag
  if (window.isSecureContext !== undefined) {
    return window.isSecureContext
  }
  
  // AGC: Fallback: check protocol
  const protocol = window.location.protocol
  const hostname = window.location.hostname
  
  // AGC: localhost is treated as secure even on HTTP
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return true
  }
  
  return protocol === 'https:'
}

// AGC: Check if WebAuthn is supported (for web only)
// AGC: Requires secure context (HTTPS) except for localhost
const isWebAuthnSupported = (): boolean => {
  // AGC: On native, use NativeBiometric instead
  if (isNativePlatform()) return false
  
  if (typeof window === 'undefined') return false
  
  // AGC: Must be secure context for WebAuthn
  if (!isSecureContext()) {
    console.log('⚠️ AGC: WebAuthn requires HTTPS. Current context is not secure.')
    return false
  }
  
  return 'PublicKeyCredential' in window &&
         typeof window.PublicKeyCredential === 'function'
}

/**
 * AGC: useBiometric hook - Handles biometric authentication
 * AGC: Uses WebAuthn for web browsers
 */
export const useBiometric = (): UseBiometricReturn => {
  const [state, setState] = useState<BiometricState>({
    isAvailable: false,
    isEnrolled: false,
    isLoading: true,
    error: null,
    biometricType: null,
  })

  // AGC: Check biometric availability on mount
  useEffect(() => {
    checkAvailability()
  }, [])

  /**
   * AGC: Check if biometric authentication is available
   */
  const checkAvailability = useCallback(async (): Promise<boolean> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }))

    try {
      // AGC: Native platform - use NativeBiometric plugin
      if (isNativePlatform()) {
        console.log('📱 AGC: Native platform detected - checking native biometric')
        return await checkNativeBiometricAvailability()
      }
      
      // AGC: Web - Check WebAuthn/Passkeys
      if (isWebAuthnSupported()) {
        return await checkWebAuthnAvailability()
      }
      
      console.log('⚠️ AGC: No biometric method available')
      setState(prev => ({
        ...prev,
        isAvailable: false,
        isLoading: false,
        biometricType: null,
      }))
      return false
    } catch (error: any) {
      console.error('❌ AGC: Error checking biometric availability:', error)
      setState(prev => ({
        ...prev,
        isAvailable: false,
        isLoading: false,
        error: error.message || 'Erro ao verificar biometria',
      }))
      return false
    }
  }, [])

  /**
   * AGC: Check native biometric availability (iOS/Android)
   */
  const checkNativeBiometricAvailability = async (): Promise<boolean> => {
    try {
      const result = await NativeBiometric.isAvailable()
      
      // AGC: Map biometry type
      let biometricType: 'fingerprint' | 'face' | 'iris' | 'unknown' | null = null
      if (result.isAvailable) {
        switch (result.biometryType) {
          case BiometryType.FINGERPRINT:
          case BiometryType.TOUCH_ID:
            biometricType = 'fingerprint'
            break
          case BiometryType.FACE_ID:
          case BiometryType.FACE_AUTHENTICATION:
            biometricType = 'face'
            break
          case BiometryType.IRIS_AUTHENTICATION:
            biometricType = 'iris'
            break
          default:
            biometricType = 'unknown'
        }
      }

      // AGC: Check if credentials exist
      let isEnrolled = false
      try {
        const credentials = await NativeBiometric.getCredentials({ server: BIOMETRIC_SERVER })
        isEnrolled = !!credentials.username
      } catch {
        // AGC: No credentials stored yet
        isEnrolled = false
      }

      setState(prev => ({
        ...prev,
        isAvailable: result.isAvailable,
        isEnrolled,
        isLoading: false,
        biometricType,
      }))

      console.log(`📱 AGC: Native biometric available: ${result.isAvailable}, type: ${biometricType}`)
      return result.isAvailable
    } catch (error: any) {
      console.error('❌ AGC: Native biometric check failed:', error)
      setState(prev => ({
        ...prev,
        isAvailable: false,
        isLoading: false,
        biometricType: null,
      }))
      return false
    }
  }

  /**
   * AGC: Check WebAuthn availability (web)
   */
  const checkWebAuthnAvailability = async (): Promise<boolean> => {
    try {
      // AGC: Check if platform authenticator is available (fingerprint, face ID)
      const available = await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
      
      // AGC: Check if user already has credentials stored
      const storedCredentialId = localStorage.getItem('droidfarm_biometric_credential')
      
      setState(prev => ({
        ...prev,
        isAvailable: available,
        isEnrolled: !!storedCredentialId,
        isLoading: false,
        biometricType: available ? 'fingerprint' : null, // AGC: Default to fingerprint for web
      }))

      console.log('🌐 AGC: WebAuthn platform authenticator available:', available)
      return available
    } catch (error: any) {
      console.error('❌ AGC: WebAuthn check failed:', error)
      setState(prev => ({
        ...prev,
        isAvailable: false,
        isLoading: false,
      }))
      return false
    }
  }

  /**
   * AGC: Enroll biometric for user
   */
  const enrollBiometric = useCallback(async (userId: string): Promise<boolean> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }))

    try {
      // AGC: Native platform - use NativeBiometric
      if (isNativePlatform()) {
        console.log('📱 AGC: Enrolling with native biometric')
        return await enrollNativeBiometric(userId)
      }
      
      // AGC: Web - Create WebAuthn credential
      if (isWebAuthnSupported()) {
        return await enrollWebAuthn(userId)
      }
      
      throw new Error('Biometria não disponível neste dispositivo')
    } catch (error: any) {
      console.error('❌ AGC: Error enrolling biometric:', error)
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error.message || 'Erro ao configurar biometria',
      }))
      return false
    }
  }, [])

  /**
   * AGC: Enroll with native biometric (iOS/Android)
   */
  const enrollNativeBiometric = async (userId: string): Promise<boolean> => {
    try {
      // AGC: First verify the user can authenticate with biometric
      await NativeBiometric.verifyIdentity({
        reason: 'Configure a biometria para login rápido',
        title: 'DroidFarm',
        subtitle: 'Autenticação biométrica',
        description: 'Use sua digital ou face para acessar o app',
      })

      // AGC: Store credentials for future authentication
      await NativeBiometric.setCredentials({
        server: BIOMETRIC_SERVER,
        username: userId,
        password: `biometric_${userId}_${Date.now()}`, // AGC: Dummy password for verification
      })

      // AGC: Update Firebase
      await updateBiometricStatus(userId, true, `native_${userId}`)

      setState(prev => ({
        ...prev,
        isEnrolled: true,
        isLoading: false,
      }))

      console.log('✅ AGC: Native biometric enrolled for user:', userId)
      return true
    } catch (error: any) {
      console.error('❌ AGC: Native biometric enrollment failed:', error)
      
      let errorMessage = 'Falha ao configurar biometria'
      if (error.message?.includes('cancel') || error.code === 'BIOMETRIC_CANCELED') {
        errorMessage = 'Configuração cancelada pelo usuário'
      } else if (error.message?.includes('not available')) {
        errorMessage = 'Biometria não disponível neste dispositivo'
      }
      
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }))
      return false
    }
  }

  /**
   * AGC: Enroll with WebAuthn (web)
   */
  const enrollWebAuthn = async (userId: string): Promise<boolean> => {
    try {
      // AGC: Create WebAuthn credential
      const challenge = new Uint8Array(32)
      crypto.getRandomValues(challenge)

      const createCredentialOptions: PublicKeyCredentialCreationOptions = {
        challenge,
        rp: {
          name: 'DroidFarm',
          id: window.location.hostname,
        },
        user: {
          id: new TextEncoder().encode(userId),
          name: userId,
          displayName: 'DroidFarm User',
        },
        pubKeyCredParams: [
          { alg: -7, type: 'public-key' }, // AGC: ES256
          { alg: -257, type: 'public-key' }, // AGC: RS256
        ],
        authenticatorSelection: {
          authenticatorAttachment: 'platform', // AGC: Use device biometric
          userVerification: 'required',
          residentKey: 'preferred',
        },
        timeout: 60000,
        attestation: 'none',
      }

      const credential = await navigator.credentials.create({
        publicKey: createCredentialOptions,
      }) as PublicKeyCredential

      if (!credential) {
        throw new Error('Falha ao criar credencial biométrica')
      }

      // AGC: Store credential ID in Firestore
      const credentialId = arrayBufferToBase64(credential.rawId)
      await updateBiometricStatus(userId, true, credentialId)

      // AGC: Also store in localStorage for quick lookup
      localStorage.setItem('droidfarm_biometric_credential', credentialId)
      localStorage.setItem('droidfarm_biometric_user', userId)

      setState(prev => ({
        ...prev,
        isEnrolled: true,
        isLoading: false,
      }))

      console.log('✅ AGC: WebAuthn credential created for user:', userId)
      return true
    } catch (error: any) {
      console.error('❌ AGC: WebAuthn enrollment failed:', error)
      
      let errorMessage = 'Falha ao configurar biometria'
      if (error.name === 'NotAllowedError') {
        errorMessage = 'Biometria cancelada pelo usuário'
      } else if (error.name === 'NotSupportedError') {
        errorMessage = 'Biometria não suportada neste dispositivo'
      }
      
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }))
      return false
    }
  }

  /**
   * AGC: Authenticate with biometric
   */
  const authenticateWithBiometric = useCallback(async (): Promise<boolean> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }))

    try {
      // AGC: Native platform - use NativeBiometric
      if (isNativePlatform()) {
        return await authenticateNativeBiometric()
      }
      
      // AGC: Web - use WebAuthn
      if (isWebAuthnSupported()) {
        return await authenticateWebAuthn()
      }
      
      throw new Error('Biometria não disponível')
    } catch (error: any) {
      console.error('❌ AGC: Biometric authentication failed:', error)
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error.message || 'Falha na autenticação biométrica',
      }))
      return false
    }
  }, [])

  /**
   * AGC: Authenticate with native biometric (iOS/Android)
   */
  const authenticateNativeBiometric = async (): Promise<boolean> => {
    try {
      // AGC: Verify stored credentials exist
      const credentials = await NativeBiometric.getCredentials({ server: BIOMETRIC_SERVER })
      
      if (!credentials.username) {
        throw new Error('Nenhuma credencial biométrica cadastrada')
      }

      // AGC: Verify identity with biometric
      await NativeBiometric.verifyIdentity({
        reason: 'Autentique para acessar o DroidFarm',
        title: 'DroidFarm',
        subtitle: 'Autenticação biométrica',
        description: 'Use sua digital ou face para continuar',
      })

      setState(prev => ({
        ...prev,
        isLoading: false,
      }))

      console.log('✅ AGC: Native biometric authentication successful')
      return true
    } catch (error: any) {
      console.error('❌ AGC: Native biometric authentication failed:', error)
      
      let errorMessage = 'Falha na autenticação biométrica'
      if (error.message?.includes('cancel') || error.code === 'BIOMETRIC_CANCELED') {
        errorMessage = 'Autenticação cancelada'
      } else if (error.message?.includes('credentials')) {
        errorMessage = 'Nenhuma credencial cadastrada'
      }
      
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }))
      return false
    }
  }

  /**
   * AGC: Authenticate with WebAuthn (web)
   */
  const authenticateWebAuthn = async (): Promise<boolean> => {
    try {
      const storedCredentialId = localStorage.getItem('droidfarm_biometric_credential')
      
      if (!storedCredentialId) {
        throw new Error('Nenhuma credencial biométrica cadastrada')
      }

      const challenge = new Uint8Array(32)
      crypto.getRandomValues(challenge)

      const getCredentialOptions: PublicKeyCredentialRequestOptions = {
        challenge,
        rpId: window.location.hostname,
        allowCredentials: [{
          id: base64ToArrayBuffer(storedCredentialId),
          type: 'public-key',
          transports: ['internal'],
        }],
        userVerification: 'required',
        timeout: 60000,
      }

      const assertion = await navigator.credentials.get({
        publicKey: getCredentialOptions,
      }) as PublicKeyCredential

      if (assertion) {
        setState(prev => ({
          ...prev,
          isLoading: false,
        }))
        console.log('✅ AGC: WebAuthn authentication successful')
        return true
      }

      throw new Error('Falha na verificação biométrica')
    } catch (error: any) {
      console.error('❌ AGC: WebAuthn authentication failed:', error)
      
      let errorMessage = 'Falha na autenticação biométrica'
      if (error.name === 'NotAllowedError') {
        errorMessage = 'Autenticação cancelada'
      }
      
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }))
      return false
    }
  }

  /**
   * AGC: Clear error state
   */
  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }))
  }, [])

  return {
    ...state,
    checkAvailability,
    enrollBiometric,
    authenticateWithBiometric,
    clearError,
  }
}

// AGC: Helper functions for ArrayBuffer <-> Base64 conversion
const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary)
}

const base64ToArrayBuffer = (base64: string): ArrayBuffer => {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes.buffer
}

export default useBiometric

